// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { Session } from "@insite/client-framework/Services/SessionService";
import translate from "@insite/client-framework/Translate";
import { InvoiceModel, InvoiceSettingsModel } from "@insite/client-framework/Types/ApiModels";
import InvoiceDetailPageTypeLink from "@insite/content-library/Components/InvoiceDetailPageTypeLink";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import Link from "@insite/mobius/Link";
import { getSettingsCollection } from "@werner/App/Store/Selectors/SettingsSelectors";
import LazyImage, { LazyImageProps } from "@werner/MobiusOverrides/LazyImage";
import { EMPTY_VALUE, sharedDateTimeOptions } from "@werner/XDL/constants";
import { ellipsis, xScreen } from "@werner/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    invoice: InvoiceModel;
    session: Session;
    extendedStyles?: InvoiceSummaryCardStyles;
}

type Props = OwnProps;

export interface InvoiceSummaryCardStyles {
    dataCell?: DataTableCellProps;
    shipToCell?: DataTableCellProps;
    billTrustLogo?: LazyImageProps;
}

export const invoiceSummaryCardStyles: InvoiceSummaryCardStyles = {
    shipToCell: {
        css: css`
            ${xScreen("gt", "md")} {
                max-width: 375px;
                ${ellipsis(2)}
                height: 60px;
                white-space: pre-wrap;
            }
            ${xScreen("lte", "md")} {
                white-space: nowrap;
                max-width: initial;
            }
        `,
    },
    billTrustLogo: {
        width: "72px",
    },
};

const InvoiceSummaryCard: FC<Props> = ({ invoice, session, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(invoiceSummaryCardStyles, extendedStyles));
    const { billTrustLogo } = useAppSelector(state => {
        return {
            billTrustLogo: getSettingsCollection(state).customSettings.billTrustImagePath,
        };
    });
    const shipToNickName = invoice.properties?.shipToNickName
        ? invoice.properties?.shipToNickName
        : invoice.stCompanyName;
    const customerNumber = invoice.customerSequence ? invoice.customerSequence : invoice.customerNumber;
    const data = {
        id: invoice.id,
        invoiceNumber: invoice.invoiceNumber,
        date: invoice.invoiceDate
            ? getLocalizedDateTime({
                  dateTime: new Date(invoice.invoiceDate),
                  language: session.language,
                  options: {
                      ...sharedDateTimeOptions,
                  },
              })
            : "",
        dueDate: invoice.dueDate
            ? getLocalizedDateTime({
                  dateTime: new Date(invoice.dueDate),
                  language: session.language,
                  options: {
                      ...sharedDateTimeOptions,
                  },
              })
            : "",
        shipTo: `${shipToNickName} #${customerNumber}`,
        total: invoice.invoiceTotalDisplay,
        customerPO: invoice.customerPO,
        currentBalance: invoice.currentBalanceDisplay,
        billTrustLink: invoice.properties?.billTrustLink ? invoice.properties?.billTrustLink : null,
        customerReleaseNumber: invoice.properties?.customerReleaseNumber,
    };

    return (
        <>
            <DataTableCell data-th={translate("Date")} {...styles.dataCell}>
                {data.date}
            </DataTableCell>
            <DataTableCell data-th={translate("Invoice #")} {...styles.dataCell}>
                <InvoiceDetailPageTypeLink
                    title={data.invoiceNumber || EMPTY_VALUE}
                    invoiceNumber={invoice.invoiceNumber}
                    aria-label={`${data.invoiceNumber || EMPTY_VALUE}`}
                />
            </DataTableCell>
            <DataTableCell data-th={translate("PO #")} {...styles.dataCell}>
                {data.customerPO || EMPTY_VALUE}
            </DataTableCell>
            <DataTableCell data-th={translate("Customer Rel #")} {...styles.dataCell}>
                {data.customerReleaseNumber || EMPTY_VALUE}
            </DataTableCell>
            <DataTableCell data-th={translate("Ship-To")} {...styles.shipToCell}>
                {data.shipTo || EMPTY_VALUE}
            </DataTableCell>
            <DataTableCell data-th={translate("Due Date")} {...styles.dataCell}>
                {data.dueDate || EMPTY_VALUE}
            </DataTableCell>
            <DataTableCell data-th={translate("Total")} {...styles.dataCell}>
                {data.total || EMPTY_VALUE}
            </DataTableCell>
            <DataTableCell data-th={translate("Balance")} {...styles.dataCell}>
                {data.currentBalance || EMPTY_VALUE}
            </DataTableCell>
            <DataTableCell data-th={translate("BillTrust")} {...styles.dataCell}>
                {data.billTrustLink ? (
                    <Link href={data.billTrustLink} target="_blank" aria-label="Bill Trust Link">
                        <LazyImage
                            src={billTrustLogo}
                            {...styles.billTrustLogo}
                            altText={translate("Bill Trust Link")}
                        />
                    </Link>
                ) : (
                    EMPTY_VALUE
                )}
            </DataTableCell>
        </>
    );
};

export default InvoiceSummaryCard;
